import { ReactNode, useRef, useState } from 'react';
import { View } from 'react-native';
import { clsx } from 'clsx';
import { Text } from 'shared/components/text';

const Tooltip = ({
  children,
  content,
  className,
  testID,
  inline = false,
}: {
  children: ReactNode;
  content: string;
  className?: string;
  testID?: string;
  inline?: boolean;
}) => {
  const tooltipRef = useRef<View & HTMLElement>(null);
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  const positionTooltip = () => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    let left = 8;
    let right = window.innerWidth - 8;

    for (let parent = tooltip.parentElement; parent; parent = parent.parentElement) {
      if (['hidden', 'clip', 'auto', 'scroll'].includes(getComputedStyle(parent).overflowX)) {
        const bounds = parent.getBoundingClientRect();
        left = Math.max(left, bounds.left + 8);
        right = Math.min(right, bounds.right - 8);
      }
    }

    const bounds = tooltip.getBoundingClientRect();
    const naturalLeft = bounds.left - horizontalOffset;
    setHorizontalOffset(
      Math.max(left - naturalLeft, Math.min(0, right - naturalLeft - bounds.width)),
    );
  };

  if (inline) {
    return (
      <span
        aria-label={content}
        className={clsx('group/tooltip relative inline-flex cursor-help align-middle', className)}
        data-testid={testID}
        onPointerEnter={positionTooltip}
      >
        {children}
        <span
          ref={tooltipRef}
          role="tooltip"
          style={{ marginLeft: horizontalOffset }}
          className={
            'border-brand-brown-dark/15 shadow-brand-brown-dark/15 pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 w-max -translate-x-1/2 rounded border bg-white px-3 py-2 opacity-0 shadow-sm transition-opacity group-hover/tooltip:opacity-100'
          }
        >
          <span
            className={
              'text-brand-brown-dark block max-w-64 whitespace-normal text-center text-xs font-medium leading-snug'
            }
          >
            {content}
          </span>
        </span>
      </span>
    );
  }

  return (
    <View
      accessibilityLabel={content}
      className={clsx('group/tooltip relative cursor-help', className)}
      testID={testID}
      onPointerEnter={positionTooltip}
    >
      {children}
      <View
        ref={tooltipRef}
        role={'tooltip'}
        style={{ marginLeft: horizontalOffset }}
        className={
          'border-brand-brown-dark/15 shadow-brand-brown-dark/15 pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 w-max -translate-x-1/2 rounded border bg-white px-3 py-2 opacity-0 shadow-sm transition-opacity group-hover/tooltip:opacity-100'
        }
      >
        <Text
          className={
            'text-brand-brown-dark max-w-64 whitespace-normal text-center text-xs font-medium leading-snug'
          }
        >
          {content}
        </Text>
      </View>
    </View>
  );
};

export { Tooltip };
